import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, 
  Users, 
  Calendar, 
  Newspaper, 
  LogOut, 
  Menu, 
  X,
  Wine,
  MessageSquare,
  FileText,
  Map,
  Settings,
  Image,
  Globe,
  Package,
  ShoppingCart,
  Truck,
  Calculator,
  Home
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const aiMenuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
    { icon: MessageSquare, label: 'Conversaciones', path: '/admin/conversations' },
    { icon: Users, label: 'Clientes', path: '/admin/customers' },
    { icon: Calendar, label: 'Reservas', path: '/admin/reservations' },
    { icon: FileText, label: 'Prompts IA', path: '/admin/prompts' },
    { icon: Map, label: 'Mapa', path: '/admin/map' },
  ];

  const adminMenuItems = [
    { icon: Package, label: 'Stock', path: '/admin/stock' },
    { icon: ShoppingCart, label: 'Ventas', path: '/admin/sales' },
    { icon: Truck, label: 'Proveedores', path: '/admin/suppliers' },
    { icon: Calculator, label: 'Contabilidad', path: '/admin/accounting' },
  ];

  const webMenuItems = [
    { icon: Newspaper, label: 'Noticias', path: '/admin/news' },
    { icon: Settings, label: 'Configuración', path: '/admin/config' },
    { icon: Image, label: 'Galería', path: '/admin/gallery' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <motion.aside
        initial={{ x: 0 }}
        animate={{ x: sidebarOpen ? 0 : -280 }}
        className="fixed top-0 left-0 h-full w-64 bg-gray-900 text-white shadow-xl z-40 transition-transform overflow-y-auto"
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-800">
          <div className="flex items-center gap-3 mb-4">
            <Wine className="h-8 w-8" style={{color: '#D4AF37'}} />
            <div>
              <h2 className="text-lg font-bold">Panel Admin</h2>
              <p className="text-xs text-gray-400">Bodega Spinoglio</p>
            </div>
          </div>
          {user && (
            <div className="text-sm">
              <p className="text-gray-400">Usuario:</p>
              <p className="font-semibold">{user.username}</p>
            </div>
          )}
        </div>

        {/* Menu Items */}
        <nav className="p-4 space-y-6">
          {/* Sección IA */}
          <div>
            <div className="flex items-center gap-2 px-4 py-3 mb-3 rounded-lg" style={{background: 'rgba(212, 175, 55, 0.15)'}}>
              <MessageSquare className="h-5 w-5" style={{color: '#D4AF37'}} />
              <h3 className="text-sm font-bold uppercase tracking-wider" style={{color: '#D4AF37'}}>Asistente IA</h3>
            </div>
            <div className="space-y-1">
              {aiMenuItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      isActive
                        ? 'text-white shadow-lg'
                        : 'text-gray-400 hover:text-white hover:bg-gray-800'
                    }`
                  }
                  style={({ isActive }) => 
                    isActive ? { background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)' } : {}
                  }
                >
                  <item.icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                </NavLink>
              ))}
            </div>
          </div>

          {/* Sección ADMINISTRACIÓN & CONTABILIDAD */}
          <div>
            <div className="flex items-center gap-2 px-4 py-3 mb-3 rounded-lg" style={{background: 'rgba(34, 197, 94, 0.15)'}}>
              <Calculator className="h-5 w-5 text-green-400" />
              <h3 className="text-sm font-bold text-green-400 uppercase tracking-wider">Administración</h3>
            </div>
            <div className="space-y-1">
              {adminMenuItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      isActive
                        ? 'text-white shadow-lg'
                        : 'text-gray-400 hover:text-white hover:bg-gray-800'
                    }`
                  }
                  style={({ isActive }) => 
                    isActive ? { background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)' } : {}
                  }
                >
                  <item.icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                </NavLink>
              ))}
            </div>
          </div>

          {/* Sección WEB */}
          <div>
            <div className="flex items-center gap-2 px-4 py-3 mb-3 rounded-lg" style={{background: 'rgba(59, 130, 246, 0.15)'}}>
              <Globe className="h-5 w-5 text-blue-400" />
              <h3 className="text-sm font-bold text-blue-400 uppercase tracking-wider">Gestión Web</h3>
            </div>
            <div className="space-y-1">
              {webMenuItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      isActive
                        ? 'text-white shadow-lg'
                        : 'text-gray-400 hover:text-white hover:bg-gray-800'
                    }`
                  }
                  style={({ isActive }) => 
                    isActive ? { background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)' } : {}
                  }
                >
                  <item.icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        </nav>

        {/* Navigation & Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-800 bg-gray-900 space-y-2">
          <button
            onClick={() => navigate('/')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-all"
          >
            <Home className="h-5 w-5" />
            <span className="font-medium">Volver al sitio</span>
          </button>
          <button
            onClick={() => navigate('/propuesta')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-all"
          >
            <FileText className="h-5 w-5" />
            <span className="font-medium">Ver Propuesta</span>
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:text-white hover:bg-red-600 transition-all"
          >
            <LogOut className="h-5 w-5" />
            <span className="font-medium">Cerrar Sesión</span>
          </button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <div 
        className="transition-all duration-300"
        style={{ marginLeft: sidebarOpen ? '256px' : '0' }}
      >
        {/* Top Bar */}
        <header className="bg-white shadow-sm sticky top-0 z-30">
          <div className="flex items-center justify-between px-6 py-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {sidebarOpen ? (
                <X className="h-6 w-6 text-gray-600" />
              ) : (
                <Menu className="h-6 w-6 text-gray-600" />
              )}
            </button>

            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-gray-900 rounded-lg transition-all text-sm font-medium"
              >
                <Home className="h-4 w-4" />
                Volver al sitio
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
